import { useState } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const SAFE_NEXT_RE = /^\/[a-zA-Z0-9/_-]*$/;

const Auth = () => {
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Compute the post-auth destination: respect ?next= when it's a safe internal
  // path, otherwise admins land on /admin and everyone else on /dashboard.
  const rawNext = searchParams.get("next");
  const safeNext = rawNext && SAFE_NEXT_RE.test(rawNext) ? rawNext : null;
  const destination = (target) => {
    if (safeNext) {
      // Don't let a non-admin be sent to /admin via ?next=
      if (safeNext === "/admin" && !target?.is_admin) return "/dashboard";
      return safeNext;
    }
    return target?.is_admin ? "/admin" : "/dashboard";
  };

  // Already signed in — declarative redirect avoids the
  // "Cannot update a component while rendering a different component" warning.
  if (user) {
    return <Navigate to={destination(user)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let signedInUser;
      if (mode === "login") {
        signedInUser = await login(formData.email, formData.password);
        toast.success("Welcome back");
      } else {
        if (!formData.name) {
          toast.error("Please enter your name");
          setLoading(false);
          return;
        }
        signedInUser = await register(formData.email, formData.password, formData.name);
        toast.success("Account created. Welcome to Somalume.");
      }
      navigate(destination(signedInUser), { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-32 flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md mx-auto px-6">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-gold mb-8 group"
        >
          <ArrowLeft size={16} className="transform group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="text-sm uppercase tracking-widest">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl text-[#EAEAEA] mb-2">
            {mode === "login" ? "Welcome Back" : "Begin Your Journey"}
          </h1>
          <p className="text-[#A1A1AA]">
            {mode === "login" 
              ? "Sign in to access your library" 
              : "Create an account to start exploring"}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
          {mode === "register" && (
            <div>
              <Label htmlFor="name" className="text-[#A1A1AA] text-sm uppercase tracking-widest mb-2 block">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-transparent border-white/10 focus:border-gold/40 text-[#EAEAEA] rounded-none h-12"
                placeholder="Your name"
                data-testid="name-input"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-[#A1A1AA] text-sm uppercase tracking-widest mb-2 block">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-transparent border-white/10 focus:border-gold/40 text-[#EAEAEA] rounded-none h-12"
              placeholder="you@example.com"
              data-testid="email-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-[#A1A1AA] text-sm uppercase tracking-widest mb-2 block">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="bg-transparent border-white/10 focus:border-gold/40 text-[#EAEAEA] rounded-none h-12"
              placeholder="••••••••"
              data-testid="password-input"
              required
              minLength={6}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 uppercase text-xs tracking-widest btn-shine"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : mode === "login" ? (
              "Sign In"
            ) : (
              "Create Account"
            )}
          </Button>
        </form>

        {/* Toggle Mode */}
        <div className="mt-8 text-center">
          <p className="text-[#A1A1AA] text-sm">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="ml-2 text-gold hover:underline"
              data-testid="toggle-auth-mode"
            >
              {mode === "login" ? "Sign Up" : "Sign In"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
